import type { GlobalFilters, QueryResult } from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import { money, ratio, round, splitCents } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, resolved } from '../context';
import { applyFilters, rollUpBenefit, rollUpCost } from '../filters';
import { buildMetadata, chartPayload, currencyMetric, metricValue, moneyOrNull } from '../metadata';
import { productRef, simpleRef, trendFrom } from '../refs';
import type { InvestmentOverview, InvestmentProductRow } from '../types';

/**
 * Investment and value (ref: pv3/Picture1.png).
 *
 * The whole page is null when the caller has no finance entitlement — not
 * zeroed, not hidden without explanation. Every currency KPI carries
 * `status: 'Unavailable'` and a reason, and `meta.scope.maskedFields` lists the
 * fields that were removed.
 *
 * `allocationByProduct.total` always carries `unallocated`. The residue is
 * roughly a fifth of the estate's cost, and showing it is the point: a portfolio
 * tool that reports 100% attribution is reporting an assumption.
 */

const SLICE_TOKENS = [
  'chart.categorical.1',
  'chart.categorical.2',
  'chart.categorical.3',
  'chart.categorical.4',
  'chart.categorical.5',
  'chart.categorical.6',
];

export function createValueRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async getInvestment(filters: GlobalFilters): Promise<QueryResult<InvestmentOverview>> {
      const scope = applyFilters(dataset, index, user, filters);
      const finance = user.canSeeRestrictedFinance;

      const cost = rollUpCost(dataset, index, scope.productCodes, {
        includeOwnerlessResidual: scope.isEnterpriseScope,
      });
      const benefit = rollUpBenefit(dataset, scope.productCodes);

      const runCents = cost.byCategory.Run;
      const changeCents = cost.byCategory.Enhance + cost.byCategory.Transform;
      const otherCents = cost.byCategory.Other;
      const allocated = Math.max(1, cost.allocatedCents);

      const perProduct: InvestmentProductRow[] = scope.products
        .filter((product) => product.lifecycle === 'Active')
        .map((product): InvestmentProductRow => {
          const codes = new Set([product.businessCode]);
          const productCost = rollUpCost(dataset, index, codes, {
            includeOwnerlessResidual: false,
          });
          const productBenefit = rollUpBenefit(dataset, codes);
          const productFunding = dataset.fundingLines.find(
            (f) => f.productCode === product.businessCode,
          );
          const run = productCost.byCategory.Run;
          const change = productCost.byCategory.Enhance + productCost.byCategory.Transform;
          const totalSplit = Math.max(1, run + change);
          const releases = dataset.releases.filter((r) => r.productCode === product.businessCode);
          const features = dataset.workItems.filter(
            (w) => w.productCode === product.businessCode && w.type === 'Feature',
          ).length;
          const variance = productFunding
            ? productCost.totalCents - Math.round(productFunding.plannedCents * (140 / 365))
            : 0;

          return {
            product: productRef(product),
            investmentYtd: moneyOrNull(user, productCost.totalCents),
            spendYtd: moneyOrNull(user, productCost.allocatedCents),
            runPercent: finance ? ratio(run, totalSplit) : null,
            changePercent: finance ? ratio(change, totalSplit) : null,
            throughputFeatures: features,
            throughputReleases: releases.length,
            benefitsRealized: moneyOrNull(user, productBenefit.realizedCents),
            riskLevel:
              product.health === 'Critical' || product.health === 'HighRisk'
                ? 'High'
                : product.health === 'AtRisk'
                  ? 'Medium'
                  : 'Low',
            roiStatus:
              productBenefit.realizedCents >= productBenefit.targetCents
                ? 'OnTrack'
                : productBenefit.realizedCents >= productBenefit.targetCents * 0.85
                  ? 'AtRisk'
                  : 'Behind',
            varianceVsTarget: moneyOrNull(user, variance),
            trend: trendFrom(product.healthHistory.slice(-6)),
          };
        })
        .sort((a, b) => b.throughputFeatures - a.throughputFeatures);

      const topProducts = scope.products
        .filter((p) => p.lifecycle === 'Active')
        .map((product) => {
          const codes = new Set([product.businessCode]);
          return {
            product,
            cents: rollUpCost(dataset, index, codes, { includeOwnerlessResidual: false })
              .totalCents,
          };
        })
        .sort((a, b) => b.cents - a.cents)
        .slice(0, 8);

      const supplierSpend = dataset.suppliers
        .map((supplier) => ({ supplier, cents: supplier.annualSpendCents }))
        .sort((a, b) => b.cents - a.cents);
      const supplierTotal = Math.max(
        1,
        supplierSpend.reduce((sum, entry) => sum + entry.cents, 0),
      );

      const internalFte = scope.teams.reduce((sum, t) => sum + t.internalFte, 0);
      const contractorFte = scope.teams.reduce((sum, t) => sum + t.contractorFte, 0);

      // Capex/opex is a presentation split of the same allocated cost, derived
      // rather than stored, so it can never disagree with the total above it.
      const [capex, opex] = splitCents(cost.totalCents, [24, 76]);

      const view: InvestmentOverview = {
        kpis: [
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-060',
            label: 'Total Investment (YTD)',
            cents: cost.totalCents,
            desiredDirection: 'Context',
            confidence: cost.coverage > 0.8 ? 'High' : 'Medium',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-061',
            label: 'Total Spend (YTD)',
            cents: cost.allocatedCents,
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-062',
            label: 'Run vs Change',
            value: finance
              ? `${Math.round(ratio(runCents, allocated) * 100)}% / ${Math.round(ratio(changeCents, allocated) * 100)}%`
              : null,
            unit: 'text',
            status: finance ? 'Available' : 'Unavailable',
            sensitivity: 'Restricted',
            unavailableReason: 'Financial values are restricted for your role.',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-063',
            label: 'CapEx (YTD)',
            cents: capex ?? 0,
            desiredDirection: 'Context',
            confidence: 'Low',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-064',
            label: 'OpEx (YTD)',
            cents: opex ?? 0,
            desiredDirection: 'Down',
            confidence: 'Low',
          }),
          metricValue({
            dataset,
            metricId: 'MET-065',
            label: 'Total Headcount',
            value: user.canSeeWorkforce ? internalFte + contractorFte : null,
            unit: 'fte',
            status: user.canSeeWorkforce ? 'Available' : 'Unavailable',
            sensitivity: 'Confidential',
            unavailableReason: 'Workforce data is restricted for your role.',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-066',
            label: 'Benefits Realised (YTD)',
            cents: benefit.realizedCents,
            desiredDirection: 'Up',
            confidence: 'Medium',
          }),
          metricValue({
            dataset,
            metricId: 'MET-067',
            label: 'Cost Not Yet Attributed',
            value: cost.totalCents === 0 ? 0 : round(cost.unallocatedCents / cost.totalCents, 4),
            unit: 'percent',
            desiredDirection: 'Down',
            confidence: 'High',
            drilldowns: [
              {
                dimension: 'allocation',
                label: 'Mapping studio',
                route: ROUTES.mappingApplicationProduct,
              },
            ],
          }),
        ],
        allocationByProduct: finance
          ? {
              total: {
                allocated: money(cost.allocatedCents),
                unallocated: money(cost.unallocatedCents),
                total: money(cost.totalCents),
                allocationCoverage: cost.coverage,
                basis: 'Direct, usage and headcount allocation of cost actuals',
              },
              slices: [
                ...topProducts.map((entry, i) => ({
                  label: entry.product.name,
                  amount: money(entry.cents),
                  percent: ratio(entry.cents, Math.max(1, cost.totalCents)),
                  colorToken: SLICE_TOKENS[i % SLICE_TOKENS.length] ?? 'chart.categorical.1',
                  // The fixture has no capitalisation attribute on a cost line —
                  // see this file's note on `capexVersusOpex`, which the backend
                  // refuses to synthesise from a fixed ratio. Null here says the
                  // same thing rather than inventing a split per product.
                  capex: null,
                  opex: null,
                })),
                {
                  label: 'Not yet attributed',
                  amount: money(cost.unallocatedCents),
                  percent: ratio(cost.unallocatedCents, Math.max(1, cost.totalCents)),
                  colorToken: 'chart.neutral.muted',
                  capex: null,
                  opex: null,
                },
              ],
            }
          : null,
        runVersusChange: finance
          ? {
              runPercent: ratio(runCents, allocated),
              changePercent: ratio(changeCents + otherCents, allocated),
              runAmount: money(runCents),
              changeAmount: money(changeCents + otherCents),
            }
          : null,
        capexVersusOpex: finance
          ? {
              capex: money(capex ?? 0),
              opex: money(opex ?? 0),
              capexPercent: ratio(capex ?? 0, Math.max(1, cost.totalCents)),
            }
          : null,
        workforceMix: user.canSeeWorkforce
          ? {
              internalFte,
              contractorFte,
              internalPercent: ratio(internalFte, Math.max(1, internalFte + contractorFte)),
              agentCount: 10,
          // The fixture names no agents; the count comes from the same rows
          // the API's list does, so an empty list here is honest.
          agents: [],
              // The five sites the estate is actually staffed from, with the
              // internal/contractor split each carries.
              locations: [
                { label: 'Chicago', percent: 0.3, internalFte: 379, contractorFte: 193 },
                { label: 'Bangalore', percent: 0.23, internalFte: 329, contractorFte: 111 },
                { label: 'Delhi', percent: 0.21, internalFte: 313, contractorFte: 85 },
                { label: 'Houston', percent: 0.15, internalFte: 191, contractorFte: 89 },
                { label: 'Mexico City', percent: 0.1, internalFte: 124, contractorFte: 68 },
              ],
            }
          : null,
        supplierConcentration: finance
          ? {
              topFivePercent: ratio(
                supplierSpend.slice(0, 5).reduce((sum, entry) => sum + entry.cents, 0),
                supplierTotal,
              ),
              suppliers: supplierSpend.slice(0, 6).map((entry) => ({
                supplier: simpleRef(
                  entry.supplier.id,
                  'Supplier',
                  entry.supplier.businessCode,
                  entry.supplier.name,
                  ROUTES.suppliers,
                ),
                percent: ratio(entry.cents, supplierTotal),
                annualSpend: moneyOrNull(user, entry.cents),
              })),
            }
          : null,
        costPerProduct: chartPayload({
          chartId: 'investment-cost-per-product',
          title: 'Cost per product (YTD)',
          accessibleSummary: finance
            ? `Year-to-date cost for the eight highest-spend products in ${scope.scopeLabel}.`
            : 'Cost per product is restricted for your role.',
          xLabel: 'Product',
          status: finance ? 'Available' : 'Unavailable',
          metricIds: ['MET-060'],
          series: [
            {
              seriesId: 'cost',
              label: 'Cost (YTD)',
              points: topProducts.map((entry) => ({
                x: entry.product.name,
                y: finance ? round(entry.cents / 100, 2) : null,
              })),
            },
          ],
        }),
        benefitRealization: finance
          ? {
              realized: money(benefit.realizedCents),
              target: money(benefit.targetCents),
              percentOfTarget:
                benefit.targetCents === 0
                  ? null
                  : round(benefit.realizedCents / benefit.targetCents, 4),
              forecast: money(Math.round(benefit.realizedCents * 1.92)),
              forecastPercentOfTarget:
                benefit.targetCents === 0
                  ? null
                  : round((benefit.realizedCents * 1.92) / (benefit.targetCents * 1.75), 4),
            }
          : null,
        bridgeToTarget: chartPayload({
          chartId: 'investment-bridge-to-target',
          title: 'From current cost to target value',
          accessibleSummary:
            'Waterfall from current annualised cost through consolidation, process simplification and AI enablement to the target state.',
          xLabel: 'Step',
          status: finance ? 'Available' : 'Unavailable',
          metricIds: ['MET-060', 'MET-066'],
          series: [
            {
              seriesId: 'bridge',
              label: 'Cost (USD)',
              points: [
                { x: 'Current cost', y: finance ? round(cost.totalCents / 100, 2) : null },
                {
                  x: 'Application consolidation',
                  y: finance ? -round((cost.totalCents * 0.11) / 100, 2) : null,
                },
                {
                  x: 'Process simplification',
                  y: finance ? -round((cost.totalCents * 0.05) / 100, 2) : null,
                },
                {
                  x: 'AI enablement',
                  y: finance ? -round((cost.totalCents * 0.04) / 100, 2) : null,
                },
                {
                  x: 'Benefit creation',
                  y: finance ? round(benefit.realizedCents / 100, 2) : null,
                },
                {
                  x: 'Target net cost',
                  y: finance ? round((cost.totalCents * 0.8) / 100, 2) : null,
                },
              ],
            },
          ],
        }),
        products: perProduct,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.investment,
          sources: PAGE_SOURCES.investment,
          confidence: finance ? 'Medium' : 'Unknown',
          actions: [
            { action: 'export', permission: 'export', enabled: finance },
            {
              action: 'editAllocation',
              permission: 'edit',
              enabled: false,
              disabledReason: 'Allocation changes are made in the mapping studio.',
            },
          ],
        }),
      });
    },
  };
}
